import { googleAuthService } from "./googleAuth";

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
  modifiedTime: string;
  createdTime: string;
  webViewLink?: string;
  webContentLink?: string;
  parents?: string[];
  trashed?: boolean;
  starred?: boolean;
  description?: string;
  thumbnailLink?: string;
  owners?: Array<{
    displayName: string;
    emailAddress: string;
    photoLink?: string;
  }>;
  permissions?: Array<{
    id: string;
    type: string;
    role: string;
    emailAddress?: string;
    displayName?: string;
  }>;
}

export interface DriveFolder {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime: string;
  createdTime: string;
  webViewLink?: string;
  parents?: string[];
  trashed?: boolean;
  starred?: boolean;
  description?: string;
}

export interface DriveConnectionStatus {
  isConnected: boolean;
  lastChecked: Date;
  error?: string;
  hasPermissions: boolean;
  quota?: {
    total: string;
    used: string;
    remaining: string;
  };
}

class DriveService {
  private connectionStatus: DriveConnectionStatus = {
    isConnected: false,
    lastChecked: new Date(),
    hasPermissions: false,
  };

  // Check drive connection status
  async checkConnectionStatus(): Promise<DriveConnectionStatus> {
    try {
      console.log("DriveService: Checking connection status...");

      // Note: Backend token validation is done by GoogleServicesService before calling this
      // Attempt Drive API call - will fail if tokens are invalid or permissions are missing
      const response = await googleAuthService.makeAuthenticatedRequest("https://www.googleapis.com/drive/v3/files?pageSize=1");

      if (response.ok) {
        // Also get quota information
        const quotaResponse = await googleAuthService.makeAuthenticatedRequest("https://www.googleapis.com/drive/v3/about?fields=storageQuota");

        let quota;
        if (quotaResponse.ok) {
          const quotaData = await quotaResponse.json();
          quota = {
            total: quotaData.storageQuota?.limit || "0",
            used: quotaData.storageQuota?.usage || "0",
            remaining: quotaData.storageQuota?.limit ? (parseInt(quotaData.storageQuota.limit) - parseInt(quotaData.storageQuota.usage || "0")).toString() : "0",
          };
        }

        this.connectionStatus = {
          isConnected: true,
          lastChecked: new Date(),
          hasPermissions: true,
          quota,
        };
      } else {
        this.connectionStatus = {
          isConnected: false,
          lastChecked: new Date(),
          hasPermissions: false,
          error: `Drive API error: ${response.status}`,
        };
      }
    } catch (error) {
      console.error("DriveService: Connection check failed:", error);
      this.connectionStatus = {
        isConnected: false,
        lastChecked: new Date(),
        hasPermissions: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }

    return this.connectionStatus;
  }

  // Get files from drive
  async getFiles(maxResults = 10, orderBy = "modifiedTime desc", query?: string): Promise<DriveFile[]> {
    try {
      console.log("DriveService: Fetching files...");

      let url = `https://www.googleapis.com/drive/v3/files?pageSize=${maxResults}&orderBy=${encodeURIComponent(orderBy)}`;

      if (query) {
        url += `&q=${encodeURIComponent(query)}`;
      }

      const response = await googleAuthService.makeAuthenticatedRequest(url);

      if (!response.ok) {
        throw new Error(`Drive API error: ${response.status}`);
      }

      const data = await response.json();
      const files: DriveFile[] = (data.files || []).map((file: any) => ({
        id: file.id,
        name: file.name,
        mimeType: file.mimeType,
        size: file.size,
        modifiedTime: file.modifiedTime,
        createdTime: file.createdTime,
        webViewLink: file.webViewLink,
        webContentLink: file.webContentLink,
        parents: file.parents,
        trashed: file.trashed,
        starred: file.starred,
        description: file.description,
        thumbnailLink: file.thumbnailLink,
        owners: file.owners,
        permissions: file.permissions,
      }));

      console.log(`DriveService: Fetched ${files.length} files`);
      return files;
    } catch (error) {
      console.error("DriveService: Failed to fetch files:", error);
      throw error;
    }
  }

  // Get folders
  async getFolders(maxResults = 10): Promise<DriveFolder[]> {
    try {
      console.log("DriveService: Fetching folders...");

      const query = "mimeType='application/vnd.google-apps.folder' and trashed=false";
      const files = await this.getFiles(maxResults, "modifiedTime desc", query);

      const folders: DriveFolder[] = files.map((file) => ({
        id: file.id,
        name: file.name,
        mimeType: file.mimeType,
        modifiedTime: file.modifiedTime,
        createdTime: file.createdTime,
        webViewLink: file.webViewLink,
        parents: file.parents,
        trashed: file.trashed,
        starred: file.starred,
        description: file.description,
      }));

      console.log(`DriveService: Fetched ${folders.length} folders`);
      return folders;
    } catch (error) {
      console.error("DriveService: Failed to fetch folders:", error);
      throw error;
    }
  }

  // Get recent files
  async getRecentFiles(maxResults = 10): Promise<DriveFile[]> {
    return this.getFiles(maxResults, "modifiedTime desc");
  }

  // Get starred files
  async getStarredFiles(maxResults = 10): Promise<DriveFile[]> {
    return this.getFiles(maxResults, "modifiedTime desc", "starred=true and trashed=false");
  }

  // Get file by ID
  async getFile(fileId: string): Promise<DriveFile> {
    try {
      console.log(`DriveService: Fetching file ${fileId}...`);

      const response = await googleAuthService.makeAuthenticatedRequest(`https://www.googleapis.com/drive/v3/files/${fileId}`);

      if (!response.ok) {
        throw new Error(`Drive API error: ${response.status}`);
      }

      const file = await response.json();
      console.log("DriveService: File fetched successfully");

      return {
        id: file.id,
        name: file.name,
        mimeType: file.mimeType,
        size: file.size,
        modifiedTime: file.modifiedTime,
        createdTime: file.createdTime,
        webViewLink: file.webViewLink,
        webContentLink: file.webContentLink,
        parents: file.parents,
        trashed: file.trashed,
        starred: file.starred,
        description: file.description,
        thumbnailLink: file.thumbnailLink,
        owners: file.owners,
        permissions: file.permissions,
      };
    } catch (error) {
      console.error("DriveService: Failed to fetch file:", error);
      throw error;
    }
  }

  // Create a new folder
  async createFolder(name: string, parentId?: string): Promise<DriveFolder> {
    try {
      console.log(`DriveService: Creating folder "${name}"...`);

      const folderMetadata = {
        name,
        mimeType: "application/vnd.google-apps.folder",
        ...(parentId && { parents: [parentId] }),
      };

      const response = await googleAuthService.makeAuthenticatedRequest("https://www.googleapis.com/drive/v3/files", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(folderMetadata),
      });

      if (!response.ok) {
        throw new Error(`Drive API error: ${response.status}`);
      }

      const folder = await response.json();
      console.log("DriveService: Folder created successfully");

      return {
        id: folder.id,
        name: folder.name,
        mimeType: folder.mimeType,
        modifiedTime: folder.modifiedTime,
        createdTime: folder.createdTime,
        webViewLink: folder.webViewLink,
        parents: folder.parents,
        trashed: folder.trashed,
        starred: folder.starred,
        description: folder.description,
      };
    } catch (error) {
      console.error("DriveService: Failed to create folder:", error);
      throw error;
    }
  }

  // Upload a file
  async uploadFile(file: File, name?: string, parentId?: string, description?: string): Promise<DriveFile> {
    try {
      console.log(`DriveService: Uploading file "${file.name}"...`);

      const metadata = {
        name: name || file.name,
        ...(parentId && { parents: [parentId] }),
        ...(description && { description }),
      };

      const formData = new FormData();
      formData.append("metadata", new Blob([JSON.stringify(metadata)], { type: "application/json" }));
      formData.append("file", file);

      const response = await googleAuthService.makeAuthenticatedRequest("https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Drive API error: ${response.status}`);
      }

      const uploadedFile = await response.json();
      console.log("DriveService: File uploaded successfully");

      return {
        id: uploadedFile.id,
        name: uploadedFile.name,
        mimeType: uploadedFile.mimeType,
        size: uploadedFile.size,
        modifiedTime: uploadedFile.modifiedTime,
        createdTime: uploadedFile.createdTime,
        webViewLink: uploadedFile.webViewLink,
        webContentLink: uploadedFile.webContentLink,
        parents: uploadedFile.parents,
        trashed: uploadedFile.trashed,
        starred: uploadedFile.starred,
        description: uploadedFile.description,
        thumbnailLink: uploadedFile.thumbnailLink,
        owners: uploadedFile.owners,
        permissions: uploadedFile.permissions,
      };
    } catch (error) {
      console.error("DriveService: Failed to upload file:", error);
      throw error;
    }
  }

  // Update file metadata
  async updateFile(
    fileId: string,
    updates: Partial<{
      name: string;
      description: string;
      parents: string[];
    }>
  ): Promise<DriveFile> {
    try {
      console.log(`DriveService: Updating file ${fileId}...`);

      const response = await googleAuthService.makeAuthenticatedRequest(`https://www.googleapis.com/drive/v3/files/${fileId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error(`Drive API error: ${response.status}`);
      }

      const updatedFile = await response.json();
      console.log("DriveService: File updated successfully");

      return {
        id: updatedFile.id,
        name: updatedFile.name,
        mimeType: updatedFile.mimeType,
        size: updatedFile.size,
        modifiedTime: updatedFile.modifiedTime,
        createdTime: updatedFile.createdTime,
        webViewLink: updatedFile.webViewLink,
        webContentLink: updatedFile.webContentLink,
        parents: updatedFile.parents,
        trashed: updatedFile.trashed,
        starred: updatedFile.starred,
        description: updatedFile.description,
        thumbnailLink: updatedFile.thumbnailLink,
        owners: updatedFile.owners,
        permissions: updatedFile.permissions,
      };
    } catch (error) {
      console.error("DriveService: Failed to update file:", error);
      throw error;
    }
  }

  // Delete a file
  async deleteFile(fileId: string): Promise<void> {
    try {
      console.log(`DriveService: Deleting file ${fileId}...`);

      const response = await googleAuthService.makeAuthenticatedRequest(`https://www.googleapis.com/drive/v3/files/${fileId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`Drive API error: ${response.status}`);
      }

      console.log("DriveService: File deleted successfully");
    } catch (error) {
      console.error("DriveService: Failed to delete file:", error);
      throw error;
    }
  }

  // Get current connection status
  getConnectionStatus(): DriveConnectionStatus {
    return { ...this.connectionStatus };
  }

  // Force check connection status
  async forceCheck(): Promise<DriveConnectionStatus> {
    return await this.checkConnectionStatus();
  }

  // Request drive permissions
  async requestPermissions(): Promise<void> {
    try {
      console.log("DriveService: Requesting drive permissions...");
      await googleAuthService.requestCalendarPermissions();
    } catch (error) {
      console.error("DriveService: Failed to request permissions:", error);
      throw error;
    }
  }
}

// Create singleton instance
export const driveService = new DriveService();
